import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { BACKOFFICE_ROLES } from '../lib/roles';

const EntityContext = createContext(null);

const ENTITY_CONFIG = {
  all: { label: 'All Entities', shortLabel: 'All', color: 'bg-gray-600', textColor: 'text-gray-700' },
  sam_rumah: { label: 'SAM Rumah', shortLabel: 'SR', color: 'bg-emerald-600', textColor: 'text-emerald-700' },
  sp_sam: { label: 'SP SAM Property', shortLabel: 'SP', color: 'bg-blue-600', textColor: 'text-blue-700' },
};

// Backoffice roles always see data across every entity (centralised in lib/roles.js).

const _detectDefaultFromHostname = () => {
  const host = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  if (host.includes('spsam') || host.includes('sp-sam')) return 'sp_sam';
  if (host.includes('samrumah') || host.includes('sam-rumah')) return 'sam_rumah';
  return 'all';
};

export const EntityProvider = ({ children }) => {
  const { user } = useAuth();

  const [currentEntity, setCurrentEntity] = useState(() => {
    const stored = localStorage.getItem('selectedEntity');
    if (stored && ENTITY_CONFIG[stored]) return stored;
    return _detectDefaultFromHostname();
  });

  // V4.17.2 — Re-evaluate `currentEntity` on EVERY user-identity change,
  // not just the first non-null user. Previously, a single `useRef` was
  // tripped on the first login and never reset on logout — so when an
  // admin logged out and an agent then logged in on the same tab, the
  // entity stayed pinned to `'all'` (admin's adjusted value) instead of
  // being reset for the agent's role. Tracking by `user.id` is correct:
  // each login mints a fresh user object so the effect re-fires.
  const lastAdjustedUserIdRef = useRef(null);
  useEffect(() => {
    if (!user) {
      // Logout — clear the guard so the next login is re-evaluated.
      lastAdjustedUserIdRef.current = null;
      return;
    }
    if (lastAdjustedUserIdRef.current === user.id) return;
    const role = (user.role || '').toLowerCase();
    if (BACKOFFICE_ROLES.has(role)) {
      setCurrentEntity('all');
      localStorage.setItem('selectedEntity', 'all');
    } else {
      // Non-backoffice user → restore the hostname-derived default so we
      // don't leak the admin's `'all'` selection into a customer/agent UI.
      const fallback = _detectDefaultFromHostname();
      setCurrentEntity(fallback);
      localStorage.setItem('selectedEntity', fallback);
    }
    lastAdjustedUserIdRef.current = user.id;
  }, [user]);

  useEffect(() => {
    localStorage.setItem('selectedEntity', currentEntity);
  }, [currentEntity]);

  const switchEntity = useCallback((entity) => {
    if (ENTITY_CONFIG[entity]) setCurrentEntity(entity);
  }, []);

  const entityParam = currentEntity === 'all' ? '' : `entity=${currentEntity}`;
  const entityQuery = currentEntity === 'all' ? {} : { entity: currentEntity };

  return (
    <EntityContext.Provider value={{ currentEntity, switchEntity, entityParam, entityQuery, config: ENTITY_CONFIG }}>
      {children}
    </EntityContext.Provider>
  );
};

export const useEntity = () => {
  const context = useContext(EntityContext);
  if (!context) throw new Error('useEntity must be used within EntityProvider');
  return context;
};

export { ENTITY_CONFIG, BACKOFFICE_ROLES };
